// src/pages/admin/candidate-status/placement.js
//
// Where a new status lands in its flow, as the create form offers it and as
// the server accepts it.
//
// `candidate_status/service.py::create_status` takes one number, `sequence`,
// and refuses everything outside a narrow band: it must sit between 1 and
// `max_seq + 1`, position 1 belongs to the INITIAL status, and an INITIAL
// status may sit nowhere else. Appending — `max_seq + 1` — is therefore the
// only placement a flow can always accept, which is why it is always offered
// and is what every unrecognised choice resolves to.

export const PLACEMENT_END = "__end__";

const maxSequence = (statuses = []) =>
  (Array.isArray(statuses) ? statuses : []).reduce(
    (highest, item) => Math.max(highest, Number(item?.sequence) || 0),
    0
  );

// The step-1 status is deliberately absent from the before-targets: placing a
// non-initial status there is refused outright ("Sequence position 1 is
// reserved for the INITIAL status"), so offering it hands the admin a choice
// that can only end in a 422.
export const buildPlacementOptions = (statuses = []) => {
  const list = (Array.isArray(statuses) ? statuses : [])
    .filter((item) => item?.id && (Number(item?.sequence) || 0) > 1)
    .sort((a, b) => (Number(a?.sequence) || 0) - (Number(b?.sequence) || 0));

  return [
    ...list.map((item) => ({
      label: `Before ${item.name} (Step ${item.sequence})`,
      value: item.id,
    })),
    {
      label: `At the end (Step ${maxSequence(statuses) + 1})`,
      value: PLACEMENT_END,
    },
  ];
};

// Always a number the server will accept for this flow. A stale id — a status
// another tab deleted — appends rather than resolving to nothing: the form
// used to leave `sequence` null there, and null is what blocked submission.
export const resolvePlacementSequence = (placement, statuses = []) => {
  const before = (Array.isArray(statuses) ? statuses : []).find(
    (item) => item?.id === placement && (Number(item?.sequence) || 0) > 1
  );

  return before ? Number(before.sequence) : maxSequence(statuses) + 1;
};
